/**
 * DashboardContainer
 *
 * 책임:
 * - useDashboard 훅으로 4개 API 호출
 * - 로딩/에러 상태 표시
 * - DashboardScreen에 데이터 전달
 *
 * AdminApp에서 screen === 'dashboard' 일 때 렌더링
 */

import { useAuth } from '../hooks/useAuth';
import { useDashboard } from '../hooks/useDashboard';
import { useSchedule } from '../hooks/useSchedule';
import { splitByCollector } from '../utils/moduleOrder';
import { DashboardScreen } from '../screens/Dashboard/DashboardScreen';
import { LoadingView } from '../../design-system/molecules/LoadingView/LoadingView';
import { useMinimumLoading } from '../hooks/useMinimumLoading';

export interface DashboardContainerProps {
  // 히트맵 셀·편성표 막대 클릭 → Job 상세 화면으로 이동 (AdminApp이 네비게이션 처리)
  onOpenJob: (jobId: number) => void;
  // 모듈 헬스 "이력" 클릭 → 수집 이력 화면으로 (해당 모듈 필터 적용)
  onOpenHistory: (moduleName: string) => void;
  // 편성표 모듈명 클릭 → 모듈 관리 화면으로 (해당 모듈로 스크롤)
  onOpenModule: (moduleName: string) => void;
}

export function DashboardContainer({ onOpenJob, onOpenHistory, onOpenModule }: DashboardContainerProps) {
  const { token } = useAuth();
  // useDashboard: 3개 API 병렬 호출 (KPI, 히트맵, 모듈 헬스)
  const { data, loading, error, retry } = useDashboard(token);
  // 편성표는 날짜 이동을 위해 별도 훅으로. (과거 날짜 조회)
  const schedule = useSchedule(token);

  // 로딩 상태 (편성표는 날짜 이동 시 부분 로딩이므로 초기 대시보드 로딩만 전체 차단)
  // 실제 로딩이 0.7초보다 빨리 끝나도 최소 0.7초는 로딩 화면을 유지.
  const showLoading = useMinimumLoading(loading);
  if (showLoading) {
    return <LoadingView message="대시보드 로딩 중…" />;
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          padding: '2rem',
          color: '#c00',
          backgroundColor: '#fee',
          borderRadius: '0.5rem',
          margin: '2rem',
        }}
      >
        <div style={{ marginBottom: '1rem' }}>❌ {error}</div>
        <button
          onClick={retry}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#c00',
            color: '#fff',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
          }}
        >
          재시도
        </button>
      </div>
    );
  }

  // 데이터 없음
  if (!data) {
    return null;
  }

  // DashboardScreen 렌더링: API 데이터 → 화면 props로 변환
  // DashboardScreen은 데이터를 받아서 각 섹션(KPI, 편성표, 히트맵, 모듈 상태)을 렌더링

  // API의 DashboardKpiResponse를 PipelineKpi[] 배열로 변환
  // 백엔드가 일부 필드를 누락해도 대시보드 전체가 죽지 않도록 옵셔널 체이닝 + 기본값 방어
  const kpi = data.kpi;
  const kpis = [
    {
      label: '모듈',
      value: (kpi?.modules ?? 0).toString(),
      caption: `활성 ${kpi?.modulesActive ?? 0} · 스케줄 ${kpi?.modulesScheduled ?? 0}`,
    },
    {
      label: '진행 중',
      value: (kpi?.running ?? 0).toString(),
      caption: 'RUNNING',
      tone: 'info' as const,
    },
    {
      label: '실패 (24h)',
      value: (kpi?.failedLast24h ?? 0).toString(),
      caption: (Array.isArray(kpi?.failedModuleNames) ? kpi.failedModuleNames : []).join(', ') || '—',
      // 0이면 success 초록, 0이 아니면 neutral(배경 없음)
      tone: ((kpi?.failedLast24h ?? 0) === 0 ? ('success' as const) : ('neutral' as const)),
    },
  ];

  // 각 패널 데이터. 누락 시 빈 배열로 방어(부분 응답 대비).
  const scheduleRows = Array.isArray(schedule.rows) ? schedule.rows : [];
  const heatmapRows = Array.isArray(data.heatmap) ? data.heatmap : [];
  const healthRows = Array.isArray(data.moduleHealth?.modules) ? data.moduleHealth.modules : [];

  // 편성표 모듈 순서를 히트맵 "화면 순서"와 동일하게 맞춘다.
  // 히트맵은 서버 cells 순서가 아니라 collectorType(csv parser 여부)로 그룹 정렬하므로,
  // 같은 공용 규칙(splitByCollector)으로 표시 순서를 재현한다.
  const { nonCsv: heatNonCsv, csv: heatCsv } = splitByCollector(heatmapRows);
  const displayOrder = [...heatNonCsv, ...heatCsv].map((r) => r.name); // 히트맵 화면 순서
  const orderIndex = new Map(displayOrder.map((name, i) => [name, i]));
  const csvNames = new Set(heatCsv.map((r) => r.name));

  // 히트맵 표시 순서로 정렬. 히트맵에 없는 모듈(실행 이력 0)은 뒤로(sort 안정성으로 원래 순서 유지).
  const sortedScheduleRows = [...scheduleRows].sort((a, b) => {
    const ai = orderIndex.get(a.name) ?? Number.MAX_SAFE_INTEGER;
    const bi = orderIndex.get(b.name) ?? Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });

  // 그룹 경계에 여백을 준다: (1) csv parser 그룹 시작 전, (2) 히트맵 밖(미실행) 그룹 시작 전.
  const firstCsvIdx = sortedScheduleRows.findIndex((r) => csvNames.has(r.name));
  const firstOtherIdx = sortedScheduleRows.findIndex((r) => !orderIndex.has(r.name));
  const orderedScheduleRows = sortedScheduleRows.map((r, i) => ({
    ...r,
    groupBreak:
      (i === firstCsvIdx && firstCsvIdx > 0) || (i === firstOtherIdx && firstOtherIdx > 0),
  }));

  return (
    <DashboardScreen
      onOpenJob={onOpenJob}
      onOpenHistory={onOpenHistory}
      onOpenModule={onOpenModule}
      onSchedulePrevDay={schedule.goPrevDay}
      onScheduleNextDay={schedule.goNextDay}
      onScheduleToday={schedule.goToday}
      data={{
        kpis,
        // 각 패널 데이터도 누락 시 빈 배열/기본값으로 대체 (부분 응답 방어)
        scheduleRows: orderedScheduleRows,
        nowHour: schedule.nowHour,
        scheduleDateLabel: schedule.dateLabel,
        scheduleIsToday: schedule.isToday,
        // heatmap은 이미 RunHeatmapRow[] (useDashboard에서 Jobs로부터 파생됨)
        heatmapRows,
        healthRows,
      }}
    />
  );
}
